import { useCallback, useState, RefObject } from 'react';
import styles from './EmbedConfigTab.module.css';
import { generateWidgetEmbedCode, WIDGET_EVENTS_API_CODE } from '../../../shared/embed';
import WebchatWidget from '../../../components/WebchatWidget';
import { WIDGET_LANGUAGE_DEFAULTS } from '../../../shared/config';

export interface EmbedConfigValues {
  primaryColor: string;
  position: string;
  title: string;
  greeting: string;
  otpGreeting: string;
  language: string;
  sessionTimeout: number;
}

interface Props {
  config: EmbedConfigValues;
  id: string | undefined;
  serverUrl: string;
  addLog: (type: string, msg: string, detail?: unknown) => void;
  widgetRef: RefObject<HTMLElement | null>
}

export default function EmbedConfigTab({ config, id, serverUrl, addLog, widgetRef }: Props) {
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [isWidgetExpanded, setIsWidgetExpanded] = useState(false);

  const copyCode = (text: string, idx: number) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  // Unified widget event handler (used by WebchatWidget's onEvent callback)
  const handleWidgetEvent = useCallback((type: string, detail: unknown) => {
    const shortName = type.replace('mcp-chat:', '');
    if (type === 'mcp-chat:expand') {
      setIsWidgetExpanded(true);
    } else if (type === 'mcp-chat:collapse') {
      setIsWidgetExpanded(false);
    } else if (type === 'mcp-chat:error') {
      const d = detail as Record<string, string> | null;
      addLog('error', `${shortName}: ${d?.technicalDetail || d?.error || 'Unknown error'}`);
    } else {
      addLog('event', shortName, detail);
    }
  }, [addLog]);

  return (
    <div className={`${styles.grid} ${isWidgetExpanded ? styles.gridExpanded : ''}`}>
      <div className={styles.left}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Installation Guide</h3>
          <p style={{ fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 1rem 0' }}>
            Add the following code before the closing <code>&lt;/body&gt;</code> tag on any page where you want the chat widget to appear.
          </p>
          <div className={styles.codeBlock}>
            <button
              className={`${styles.copyBtn} ${copiedIdx === 0 ? styles.copied : ''}`}
              onClick={() => copyCode(generateWidgetEmbedCode({
                serverUrl,
                widgetId: id,
                primaryColor: config.primaryColor,
                position: config.position,
                title: config.title,
                greeting: config.greeting,
                otpGreeting: config.otpGreeting,
                language: config.language,
                sessionTimeout: config.sessionTimeout,
              }), 0)}
            >
              {copiedIdx === 0 ? 'Copied!' : 'Copy'}
            </button>
            <pre>{generateWidgetEmbedCode({
              serverUrl,
              widgetId: id,
              primaryColor: config.primaryColor,
              position: config.position,
              title: config.title,
              greeting: config.greeting,
              otpGreeting: config.otpGreeting,
              language: config.language,
              sessionTimeout: config.sessionTimeout,
            })}</pre>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.6, margin: '1rem 0 0 0' }}>
            <strong>CDN hosted:</strong> Replace the script <code>src</code> with your deployed widget URL.<br />
            <strong>Self-hosted:</strong> Build with <code>npm run build:widget</code> and serve the output JS file.
          </p>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Portal Link</h3>
          {id ? (
            <div className={styles.codeBlock}>
              <button
                className={`${styles.copyBtn} ${copiedIdx === 2 ? styles.copied : ''}`}
                onClick={() => copyCode(`${window.location.origin}/portal/${id}`, 2)}
              >
                {copiedIdx === 2 ? 'Copied!' : 'Copy'}
              </button>
              <pre>{`${window.location.origin}/portal/${id}`}</pre>
            </div>
          ) : (
            <p style={{ fontSize: '0.8125rem', color: '#6b7280' }}>Save the widget first to get a portal link.</p>
          )}
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Events API</h3>
          <p style={{ fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 0.5rem 0' }}>
            Listen to widget events for integration with your app:
          </p>
          <div className={styles.codeBlock}>
            <button
              className={`${styles.copyBtn} ${copiedIdx === 1 ? styles.copied : ''}`}
              onClick={() => copyCode(WIDGET_EVENTS_API_CODE, 1)}
            >
              {copiedIdx === 1 ? 'Copied!' : 'Copy'}
            </button>
            <pre>{WIDGET_EVENTS_API_CODE}</pre>
          </div>
        </div>
      </div>
      {/* Right: Live Preview */}
      <div className={styles.previewArea}>
        <span className={styles.previewLabel}>Preview</span>
        <WebchatWidget
          ref={widgetRef}
          serverUrl={serverUrl}
          auth="none"
          preview
          widgetId={id}
          primaryColor={config.primaryColor}
          position={config.position}
          language={config.language}
          title={config.title && (!WIDGET_LANGUAGE_DEFAULTS[config.language] || config.title !== WIDGET_LANGUAGE_DEFAULTS[config.language].title) ? config.title : undefined}
          greeting={config.greeting && (!WIDGET_LANGUAGE_DEFAULTS[config.language] || config.greeting !== WIDGET_LANGUAGE_DEFAULTS[config.language].greeting) ? config.greeting : undefined}
          otpGreeting={config.otpGreeting || undefined}
          sessionTimeout={config.sessionTimeout}
          onEvent={handleWidgetEvent}
          style={{ '--mcp-panel-height': '460px', '--mcp-panel-max-height': '460px' } as React.CSSProperties}
        />
      </div>
    </div>
  );
}
